<template>
    <div>
        <v-select
            ref="vselect"
            :options="sortOptions"
            :filterable="false"
            @search="onSearch"
            @input="onChange"
            v-bind="$attrs"
            v-on="$listeners"
            :value="value"
            :searchable="true"
            @search:blur="setSearchText"
            @search:focus="fillDropdown"
            :class="classNames"
            :getOptionKey="
                opt => {
                    if (clearOnSelect) {
                        return opt.id;
                    } else {
                        return opt.key;
                    }
                }
            "
        >
            <!-- By setting the option key to different key, when user reselect the selected option, the input event will be triggered -->
            <template slot="no-options">
                <span v-if="search_text && search_text.length < 2"
                    >Type to search</span
                >
                <span v-else-if="!loading">No matches found</span>
                <span
                    v-else-if="
                        loading &&
                        (module === 'patient' || module === 'articles')
                    "
                >
                    Loading...
                </span>
                <span v-else></span>
            </template>
            <template
                #selected-option="{ label, mobile }"
                v-if="module === 'client-mobile'"
            >
                <div style="display: flex; align-items: baseline;">
                    {{ label }}
                    <em v-if="mobile" style="margin-left: 0.5rem;"
                        >({{ mobile }})</em
                    >
                </div>
            </template>
            <template #list-header v-if="showMenu">
                <div class="menu">
                    <a
                        v-if="
                            (module === 'client' ||
                                module === 'client-all-contacts' ||
                                module === 'client-contact' ||
                                module === 'client-mobile' ||
                                module === 'clients-include-inactive') &&
                            _hasAccess('client.profile.create')
                        "
                        @click.stop="addClient"
                        class="badge badge-success rounded-0"
                        >Add Client</a
                    >&nbsp;
                    <a
                        v-if="
                            (module === 'client-patient' ||
                                module === 'client-patient-all') &&
                            _hasAccess('patient.profile.create')
                        "
                        @click.stop="addPatient"
                        class="badge badge-success rounded-0"
                        >Add Patient</a
                    >
                    <a
                        v-if="
                            (module == 'patient' ||
                                module == 'patient-full-details') &&
                            _hasAccess('patient.profile.create')
                        "
                        @click.stop="addPatient"
                        class="badge badge-success rounded-0"
                        >Add Patient</a
                    >
                    <a
                        v-if="
                            _hasAccess('contact.person.create') &&
                            (module == 'referrer' ||
                                module == 'contacts' ||
                                module == 'client-all-contacts' ||
                                module === 'client-contact')
                        "
                        @click.stop="addContact"
                        class="badge badge-success rounded-0"
                        >Add Contact</a
                    >&nbsp;
                    <a
                        v-if="
                            _hasAccess('contact.company.create') &&
                            (module === 'referrer' ||
                                module == 'contacts' ||
                                module == 'client-all-contacts')
                        "
                        @click.stop="addCompany"
                        class="badge badge-success rounded-0"
                        >Add Company</a
                    >
                    <a
                        v-if="
                            [
                                'item',
                                'rate-boarding',
                                'wellness-element'
                            ].includes(module) &&
                            !filterByOnlinePharmacy &&
                            _hasAccess('inventory.item.create')
                        "
                        @click.stop="addItem"
                        class="badge badge-success rounded-0"
                        >Add Item</a
                    >
                    <a
                        v-if="module === 'payment-method'"
                        @click.stop="addPaymentMethod"
                        class="badge badge-success rounded-0"
                        >Add New</a
                    >
                    <a
                        v-if="module === 'diagnosis'"
                        @click.stop="addPatientDiagnosis"
                        class="badge badge-success rounded-0"
                        >Add New</a
                    >
                    <a
                        v-if="module === 'petcare-onboarding-payment-method'"
                        @click.stop="onboardingAddPaymentMethod"
                        class="badge badge-success rounded-0"
                        >Add New</a
                    >
                    {{ search_text }}
                </div>
            </template>
            <div slot="option" v-if="module === 'client'" slot-scope="option">
                <span v-if="option && option.mobile && withMobile">
                    {{ option.label }}:
                    <span v-if="option.mobile_country_code"
                        >({{ option.mobile_country_code }})</span
                    >
                    {{ option.mobile }}
                </span>
                <span v-else-if="option && option.home && withMobile">
                    {{ option.label }}:
                    <span v-if="option.home_country_code"
                        >({{ option.home_country_code }})</span
                    >
                    {{ option.home }}
                </span>
                <span v-else-if="option && option.work && withMobile">
                    {{ option.label }}:
                    <span v-if="option.work_country_code"
                        >({{ option.work_country_code }})</span
                    >
                    {{ option.work }}
                </span>
                <span v-else>{{ option.label }}</span>
            </div>
            <div
                slot="option"
                v-else-if="module === 'navbar-search-client'"
                slot-scope="option"
            >
                <span
                    ><b>{{ option.label }} ({{ option.index }})</b></span
                >
            </div>
            <div
                slot="option"
                v-else-if="module === 'patient-full-details'"
                slot-scope="option"
            >
                <span>
                    {{ option.fullname }} ({{ option.index }}) -
                    {{ getEntityBreed(option) }}</span
                >
            </div>
            <div
                slot="option"
                v-else-if="module === 'client-mobile'"
                slot-scope="option"
            >
                <span>
                    {{ option.label }}
                    <em>
                        {{ option.mobile ? "(" + option.mobile + ")" : "" }}</em
                    >
                </span>
            </div>
            <div v-else-if="module === 'diagnosis'" slot-scope="option">
                <span>{{ option.term }}</span>
            </div>
            <div
                slot="option"
                v-else-if="module === 'patient'"
                slot-scope="option"
            >
                <span>{{ option.fullname }} ({{ option.index }})</span>
            </div>
            <div
                slot="option"
                v-else-if="module === 'item' || module === 'item-lab'"
                slot-scope="option"
            >
                <span
                    :class="isOptionBold ? 'font-weight-bold' : ''"
                    style="white-space: normal;"
                >
                    {{ option.name }}
                </span>
            </div>
            <div
                slot="option"
                v-else-if="module === 'articles'"
                slot-scope="option"
                style="padding-top: 3px;"
            >
                <span>{{ option.label }}<br /></span>
                <div id="articleSummary">
                    {{ option.summary.slice(0, 100) }}...
                </div>
                <div class="divider"></div>
            </div>
            <div
                slot="option"
                v-else-if="module === 'item-po'"
                slot-scope="option"
            >
                <span style="white-space: normal;">
                    {{ option.display_name }}
                </span>
            </div>
            <div
                slot="option"
                v-else-if="module === 'client-patient-all' && showSpecies"
                slot-scope="option"
            >
                <span style="white-space: normal;">
                    {{ patientNameWithSpecies(option) }}
                </span>
            </div>
            <div
                slot="option"
                v-else-if="module === 'clients-include-inactive'"
                slot-scope="option"
            >
                <span v-if="option && option.mobile && withMobile">
                    {{ option.label }}:
                    <span v-if="option.mobile_country_code">
                        ({{ option.mobile_country_code }})
                    </span>
                    {{ option.mobile }}
                </span>
                <span v-else-if="option && option.home && withMobile">
                    {{ option.label }}:
                    <span v-if="option.home_country_code">
                        ({{ option.home_country_code }})
                    </span>
                    {{ option.home }}
                </span>
                <span v-else-if="option && option.work && withMobile">
                    {{ option.label }}:
                    <span v-if="option.work_country_code">
                        ({{ option.work_country_code }})
                    </span>
                    {{ option.work }}
                </span>
                <span v-else-if="option && option.email && withMobile">
                    {{ option.label }}:
                    {{ option.email }}
                </span>
                <span v-else-if="option && option.index && withMobile">
                    {{ option.label }}:
                    {{ option.index }}
                </span>
                <span v-else>{{ option.label }}</span>
            </div>
            <div
                slot="option"
                v-else-if="module === 'bundle'"
                slot-scope="option"
            >
                <span style="white-space: normal;">
                    {{ option.name }}
                </span>
            </div>
        </v-select>
        <NewClient
            ref="newClientWindow"
            @saved="onChange({ ...$event, is_new: true })"
            :hideBackdrop="hideBackdrop"
            is-new
        ></NewClient>
        <NewPatient
            :showAddNew="false"
            :enableRecordSharing="true"
            :clientId="clientId"
            ref="newPatientWindow"
            @saved="onChange"
            :hideBackdrop="hideBackdrop"
        ></NewPatient>
        <NewContact ref="newContactWindow" @saved="onChange"></NewContact>
        <NewCompany ref="newCompanyWindow" @saved="onChange"></NewCompany>
        <NewPaymentMethod
            ref="newPaymentMethodWindow"
            @reload="onChange"
        ></NewPaymentMethod>
        <NewPatientDiagnosis ref="newPatientDiagnosis" @saved="onChange" />
        <NewItem ref="newItemWindow" @saved="onChange"></NewItem>
        <NewItemFee
            ref="newItemFeeWindow"
            @saved="onChange"
            :isWellnessPlan="isWellnessPlan"
        ></NewItemFee>
        <ModalPaymentMethodAdd
            ref="modalPaymentMethodAdd"
            @getPaymentMethods="updatePetcareOnboardingPaymentMethod"
            @updateIsLoadingAddModal="updateIsLoadingAddModal"
        />
    </div>
</template>
<style>
#articleSummary {
    font-size: 10px;
    font-style: italic;
    white-space: pre-wrap;
}

.divider {
    height: 0;
    margin-top: 0.7rem;
    margin-bottom: -0.22rem;
    overflow: hidden;
    border-top: 1px solid #aaaa;
}
</style>
<script>
import vSelect from "vue-select";
import Fuse from "fuse.js";
import { debounce, get, keys, filter, differenceBy, find, clone } from "lodash";
import NewClient from "./client/modal.vue";
import NewPatient from "./patient/modal.vue";
import NewContact from "./contact/modal.vue";
import NewCompany from "./company/modal.vue";
import NewPaymentMethod from "@/components/payment-methods/payment-method-save";
import NewPatientDiagnosis from "@/module/patient/dashboard/custom-diagnoses/modal-new-edit";
import NewItem from "./inventory/modal.vue";
import NewItemFee from "./inventory/item-fee-modal.vue";
import * as humanparser from "humanparser";
const entities = require("entities");
import ModalPaymentMethodAdd from "@/components/setting-subscription/payment-method-add.vue";

export default {
    components: {
        vSelect,
        NewClient,
        NewPatient,
        NewContact,
        NewCompany,
        NewPaymentMethod,
        NewPatientDiagnosis,
        NewItem,
        NewItemFee,
        ModalPaymentMethodAdd
    },
    props: {
        module: {
            String,
            required: true,
            validator: function (value) {
                // The value must match one of these strings
                return (
                    [
                        "client",
                        "navbar-search-client",
                        "client-patient",
                        "client-patient-all",
                        "client-contact",
                        "client-mobile",
                        "clients-include-inactive",
                        "patient",
                        "patient-treament-board",
                        "patient-full-details",
                        "patient-breed",
                        "staff",
                        "referrer",
                        "contacts",
                        "client-all-contacts",
                        "payment-method",
                        "item",
                        "item-lab",
                        "diagnosis",
                        "articles",
                        "supplier",
                        "manufacturer",
                        "address-po", // purchase-order address
                        "item-po", // purchase-order items
                        "rate-boarding",
                        "bundle",
                        "wellness-element",
                        "petcare-onboarding-payment-method"
                    ].indexOf(value) !== -1
                );
            }
        },
        selectProps: {
            type: Object
        },
        source: {
            type: String
        },
        payer: {
            type: Object
        },
        isExtension: {
            type: String
        },
        clientId: {
            type: String
        },
        ccProcessor: {
            type: String
        },
        value: [Object, String, Array, Number],
        options: {
            type: Array,
            default: () => []
        },
        showMenu: {
            type: Boolean,
            default: true
        },
        /**
         * for hide/exclude option(s) based on "id".
         * user won't able to see/select the options
         */
        excludedId: {
            type: [String, Array],
            default: null
        },
        hideBackdrop: {
            type: Boolean,
            default: false
        },
        extra: {
            type: Object,
            default: () => ({})
        },
        /**
         * for "client-all-contacts",
         * hide type after name, eg: "Luqman (client)"
         * if true, remove "(client)"
         */
        hideSuffix: {
            type: Boolean,
            default: false
        },
        field: {
            type: String,
            default: ""
        },
        /**
         * source catalog is used for purchase order item listing
         * based on supplier code
         * (module: item-po)
         */
        sourceCatalog: {
            type: [String, null],
            default: null
        },
        showActions: {
            type: Boolean,
            default: false
        },
        withMobile: {
            type: Boolean,
            default: true
        },
        showSpecies: {
            type: Boolean,
            default: false
        },
        isMergeItem: {
            type: Boolean,
            default: false
        },
        clearOnSelect: {
            type: Boolean,
            default: true
        },
        showVetsource: {
            type: Boolean,
            default: false
        },
        showVetcoveOnline: {
            type: Boolean,
            default: false
        },
        isOptionBold: {
            type: Boolean,
            default: false
        },
        // status of the model to be return
        // ex: patient (active, inactive, deceased)
        status: {
            type: Array,
            default: () => []
        },
        /**
         * Applicable module: Client
         * Desc: To exclude "No Owner" from list
         */
        excludeNoOwner: {
            type: Boolean,
            default: false
        },
        isAddItemFee: {
            type: Boolean,
            default: false
        },
        filterByOnlinePharmacy: {
            type: Boolean,
            default: false
        },
        patientId: {
            type: String,
            default: ""
        },
        isWellnessPlan: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            search_text: "",
            remote_options: [],
            prefetch_options: [],
            txt: "",
            loading: false,
            subscription: {},
            key: 0,
            indexAxios: 0,
            abortController: []
        };
    },
    computed: {
        sortOptions() {
            let self = this;
            let results = self.prefetch_options.length
                ? self.prefetch_options
                : self.remote_options.length
                ? self.remote_options
                : self.options.length
                ? self.options
                : [];

            results = this.filterExclude(results);

            if (!this.clearOnSelect) {
                results = results.map(r => {
                    r.key = ++this.key;
                    return r;
                });
            }
            if (this.module === "client-all-contacts" && this.hideSuffix) {
                results = this.removeSuffix(results);
            }

            if (
                self.search_text &&
                !["item", "item-lab"].includes(this.module) // skip modules
            ) {
                const searcher = new Fuse(results, {
                    shouldSort: true,
                    keys: [
                        "label",
                        "name",
                        "email",
                        "mobile",
                        "work",
                        "home",
                        "term",
                        "index"
                    ]
                });
                return searcher.search(self.search_text);
            } else {
                return results;
            }
        },
        classNames() {
            let classes = [];

            if (this.showActions) {
                classes.push("show-actions");
            }

            return classes.join(" ");
        }
    },
    watch: {
        payer(value) {
            if (value && this.module === "payment-method") {
                this.searchModule("", this);
            }
        },
        clientId(value) {
            if (this.module === "client-patient" && value) {
                this.getClientPatient();
            } else if (this.module === "client-patient-all" && value) {
                this.getClientPatient(true);
            }
        },
        value(value) {
            // Append the selected option from parent to the options
            if (value && !this.clearOnSelect) {
                this.appendSelectedOptiontoOptions(value);
            }
        }
    },
    async mounted() {
        if (
            this.module === "client-patient" ||
            this.module === "client-patient-all"
        ) {
            this._getSubscription().then(data => {
                this.subscription = data;
            });
        }

        if (this.module === "payment-method") {
            this.searchModule("", this);
        }

        if (this.module === "client-patient" && this.clientId) {
            this.$emit('isLoading', true);
            await this.getClientPatient();
            this.$emit('isLoading', false);
        } else if (this.module === "client-patient-all" && this.clientId) {
            this.getClientPatient(true);
        }

        if (this.module === "address-po") {
            this.getAddressPO();
        }
    },
    methods: {
        fillDropdown() {
            // trigger search on focus to imitate CI behaviour
            // currently implemented for payment-method only, add into the switch case if needed
            switch (this.module) {
                case "payment-method":
                    this.onSearch(this.value.label);
                    this.$emit("input", this.value);
                    break;
            }
        },
        focus() {
            this.$refs.vselect.$el.querySelector("input").focus();
        },
        removeSuffix(arr = []) {
            for (let record of arr) {
                let str = record.label || "";
                str = str.replace("(contact)", "");
                str = str.replace("(client)", "");
                str = str.replace("(company)", "");
                str = str.trim();
                record.label = str;
            }

            return arr;
        },
        filterExclude(result = []) {
            if (!this.excludedId) {
                // if no excluded prop, return as is
                return result;
            } else if (typeof this.excludedId === "string") {
                return result.filter(r => r.id !== this.excludedId);
            } else if (Array.isArray(this.excludedId)) {
                return differenceBy(result, this.excludedId, "id");
            } else {
                return result;
            }
        },
        onSearch(search) {
            let minLength = ["item", "item-lab"].includes(this.module) ? 3 : 2;
            this.remote_options = [];
            this.search_text = search;
            if (search.length >= minLength) {
                this.loading = true;
                if (
                    this.abortController.length > 0 &&
                    this.abortController[this.indexAxios]
                ) {
                    // to cancel the previous search API
                    this.abortController[this.indexAxios].abort();
                }
                this.indexAxios++;
                this.searchModule(search, this, this.indexAxios);
            }
        },
        searchModule: debounce(function (search, vm, indexAxios) {
            let promise = null;
            let vetItemProps = vm.selectProps;
            let params;
            let fieldsParams = "";
            let itemPurchaseOrderURI = `${process.env.CI_APPS}ajax/inventory/`;
            let checkSearch = "";
            let clientSearchEndpoint = "";
            let isPoc = null;

            // initialize the abortcontroller
            vm.abortController[indexAxios] = new AbortController();

            if (this.field !== "") {
                fieldsParams = "&field=" + this.field;
            }

            if (this.module === "item-po") {
                if (this.sourceCatalog) {
                    itemPurchaseOrderURI += `filter_${this.sourceCatalog}_catalogs`;
                    itemPurchaseOrderURI = itemPurchaseOrderURI.toLowerCase();
                } else {
                    itemPurchaseOrderURI += "filter_all_catalogs";
                }
            }

            let filterItemEndpoint = "index.php/ajax/item/filter_items?id=";
            if (this.isMergeItem) {
                filterItemEndpoint =
                    "index.php/ajax/item/filter_items_merge_item?id=";
            }

            switch (vm.module) {
                case "patient-breed":
                    params = { query: encodeURIComponent(search) };
                    if (vm.value.length > 0) {
                        params.species = vm.value[0].species_id;
                    }

                    promise = vm.$http
                        .get(process.env.BARRAMUNDI + "patient/breed-search", {
                            params
                        })
                        .then(res => {
                            vm.remote_options = get(
                                res,
                                "data.response.resources",
                                []
                            );
                            return vm.remote_options;
                        });
                    break;

                case "client":
                case "client-mobile":
                case "navbar-search-client":
                    //check if number only, then remove all space
                    checkSearch = search.replaceAll(/\s/g, "");
                    if (/^[0-9]+$/.test(checkSearch)) {
                        search = checkSearch;
                    }

                    if (vm.excludeNoOwner === true) {
                        clientSearchEndpoint = `${process.env.ACTOR_SERVICE}client-search?exclude_default=1&reminder_options&query=${encodeURIComponent(search)}`; // prettier-ignore
                    } else {
                        clientSearchEndpoint = `${process.env.ACTOR_SERVICE}client-search?reminder_options&query=${encodeURIComponent(search)}`; // prettier-ignore
                    }

                    promise = vm.$http.get(clientSearchEndpoint).then(res => {
                        vm.remote_options = get(
                            res,
                            "data.response.resource",
                            []
                        );
                        return vm.remote_options;
                    });

                    break;

                case "clients-include-inactive":
                    promise = vm.$http
                        .get(
                            process.env.ACTOR_SERVICE +
                                "client-search?reminder_options&include_inactive=true&query=" +
                                encodeURIComponent(search)
                        )
                        .then(res => {
                            vm.remote_options = get(
                                res,
                                "data.response.resource",
                                []
                            );
                            return vm.remote_options;
                        });

                    break;

                case "item":
                    if (this.filterByOnlinePharmacy) {
                        filterItemEndpoint =
                            process.env.BARRAMUNDI +
                            "record/recommendation/product-search?keyword=" +
                            encodeURIComponent(search) +
                            "&patient_id=" +
                            this.patientId;
                    } else {
                        filterItemEndpoint =
                            process.env.APPS +
                            filterItemEndpoint +
                            vetItemProps.id +
                            "&item_type=" +
                            vetItemProps.item_type +
                            "&module=" +
                            vetItemProps.module +
                            "&status=" +
                            vetItemProps.status +
                            "&start=" +
                            vetItemProps.start +
                            "&image_source=" +
                            vetItemProps.source +
                            "&show_vetsource=" +
                            this.showVetsource +
                            "&show_vetcove_online=" +
                            this.showVetcoveOnline +
                            "&search_key=" +
                            encodeURIComponent(search);
                    }

                    // 1447 - apply abort controller
                    promise = vm.$http
                        .get(
                            filterItemEndpoint,
                            { signal: vm.abortController[indexAxios].signal } // send abortcontroller signal
                        )
                        .then(res => {
                            if (this.filterByOnlinePharmacy) {
                                const response = res.data.response;

                                if (
                                    response &&
                                    response.messages &&
                                    response.code === 200
                                ) {
                                    vm.remote_options = response.resource.map(
                                        e => ({
                                            ...e,
                                            due_date_instring: null,
                                            dose: 1,
                                            instruction: ""
                                        })
                                    );
                                }
                            } else {
                                vm.remote_options = get(res, "data", []);
                            }

                            return (vm.remote_options = _.map(
                                vm.remote_options,
                                item => ({
                                    ...item,
                                    label: entities.decodeHTML(item.name)
                                })
                            ));
                        });

                    break;
                case "item-lab":
                    if (this.value.length > 0) {
                        isPoc = this.value[0].masterItem?.code;
                    }

                    promise = vm.$http
                        .get(
                            process.env.APPS +
                                "index.php/ajax/item/filter_labs?search_key=" +
                                search +
                                "&page=1&source_scope=" +
                                vetItemProps.source +
                                "&start=" +
                                vetItemProps.start +
                                "&isPoc=" +
                                isPoc
                        )
                        .then(res => {
                            vm.remote_options = get(res, "data.form_data", []);
                            return (vm.remote_options = _.map(
                                vm.remote_options,
                                item => ({
                                    ...item,
                                    label: entities.decodeHTML(item.name)
                                })
                            ));
                        });

                    break;
                case "patient":
                case "patient-full-details":
                    promise = vm.$http
                        .get(
                            process.env.BARRACUDA +
                                "/patient/search-patient?query=" +
                                search
                        )
                        .then(res => {
                            vm.remote_options = get(
                                res,
                                "data.response.resources",
                                []
                            );

                            return vm.remote_options;
                        });

                    break;
                case "patient-treament-board":
                    promise = vm.$http
                        .get(
                            process.env.BARRACUDA +
                                "/patient/search-patient?treatment_board=1&query=" +
                                search
                        )
                        .then(res => {
                            vm.remote_options = get(
                                res,
                                "data.response.resources",
                                []
                            );

                            vm.remote_options = vm.remote_options.filter(
                                option => !option.in_treatment_board
                            );

                            return vm.remote_options;
                        });

                    break;
                case "client-all-contacts":
                case "referrer":
                    promise = vm.$http
                        .get(
                            process.env.ACTOR_SERVICE +
                                "contact-search?referrer&query=" +
                                encodeURIComponent(search)
                        )
                        .then(res => {
                            vm.remote_options = get(
                                res,
                                "data.response.resource",
                                []
                            );
                            return vm.remote_options;
                        });

                    break;
                case "contacts":
                    promise = vm.$http
                        .get(
                            process.env.ACTOR_SERVICE +
                                "contact-search?referrer&query=" +
                                encodeURIComponent(search)
                        )
                        .then(res => {
                            vm.remote_options = get(
                                res,
                                "data.response.resource",
                                []
                            );
                            vm.remote_options = filter(
                                vm.remote_options,
                                function (option) {
                                    return !option.label.includes("(client)");
                                }
                            );

                            return vm.remote_options;
                        });

                    break;
                case "payment-method":
                    if (get(vm, "payer.id")) {
                        promise = vm.$http
                            .get(
                                process.env.PAYMENT_SERVICE +
                                    "payment-method-by-client?client_id=" +
                                    vm.payer.id +
                                    "&query=" +
                                    encodeURIComponent(search)
                            )
                            .then(res => {
                                vm.remote_options = get(
                                    res,
                                    "data.response.resource",
                                    []
                                );
                                return vm.remote_options;
                            });
                    }
                    break;
                case "client-contact":
                    promise = vm.$http
                        .get(
                            process.env.BARRAMUNDI +
                                "actor/client-contact-search?with_type=true&query=" +
                                encodeURIComponent(search) +
                                fieldsParams
                        )
                        .then(res => {
                            vm.remote_options = get(
                                res,
                                "data.response.resource",
                                []
                            );
                            return vm.remote_options;
                        });

                    break;
                case "diagnosis":
                    promise = vm.$http
                        .get(
                            `${process.env.BARRAMUNDI}reference/search-diagnosis?query=${search}`
                        )
                        .then(res => {
                            vm.remote_options = get(
                                res,
                                "data.response.resources",
                                []
                            );
                            return vm.remote_options;
                        });
                    break;
                case "articles":
                    promise = vm.$http
                        .get(
                            `${process.env.BARRAMUNDI}record/attachment/lifelearn/search-article?keywords=${search}`
                        )
                        .then(res => {
                            let results = get(
                                res,
                                "data.response.resource",
                                []
                            );

                            let JSONResults = JSON.parse(results);

                            this.remote_options = [];
                            $.each(JSONResults, function (i, v) {
                                if (i != "article_count") {
                                    vm.remote_options.push({
                                        id: i,
                                        label:
                                            find(v, "title").title +
                                            " (" +
                                            i +
                                            ")",
                                        title: find(v, "title").title,
                                        summary: find(v, "summary").summary
                                    });
                                }
                            });
                            return vm.remote_options;
                        });
                    break;
                case "supplier":
                    promise = vm.$http
                        .get(
                            `${process.env.BARRAMUNDI}inventory/supplier/list?search=${search}`
                        )
                        .then(res => {
                            let arr = [];
                            let rawlist = get(
                                res,
                                "data.response.resources",
                                []
                            );

                            for (let id in rawlist) {
                                arr.push({
                                    value: id,
                                    label: rawlist[id]
                                });
                            }

                            this.remote_options = arr;
                        });
                    break;
                case "manufacturer":
                    promise = vm.$http
                        .get(
                            `${process.env.BARRAMUNDI}inventory/search-purchase-manufacturer`,
                            {
                                params: {
                                    search: search
                                }
                            }
                        )
                        .then(res => {
                            let rawlist = get(
                                res,
                                "data.response.resources",
                                []
                            );

                            let arr = rawlist.map(item => ({
                                value: item.manufacturer,
                                label: item.manufacturer
                            }));

                            vm.remote_options = arr;
                        });
                    break;
                case "item-po":
                    promise = vm.$http
                        .get(`${itemPurchaseOrderURI}?search_key=${search}`)
                        .then(res => {
                            let options = res.data || [];

                            // append "label" key
                            vm.remote_options = options.map(item => ({
                                ...item,
                                label: item.display_name
                            }));
                        });
                    break;
                case "rate-boarding":
                    promise = vm.$http
                        .get(`${process.env.BARRAMUNDI}inventory/filterItems`, {
                            params: {
                                query: search,
                                status: 1,
                                module: "BOARDING"
                            }
                        })
                        .then(res => {
                            let options =
                                res.data?.response?.resource?.results ?? [];

                            vm.remote_options = options.map(item => ({
                                ...item,
                                label: item.display_name
                            }));
                        });
                    break;
                case "bundle":
                    promise = vm.$http
                        .get(
                            `${process.env.CI_APPS}ajax/item/filter_items?module=bundle&search_key=${search}`
                        )
                        .then(res => {
                            let options = res.data || [];

                            vm.remote_options = options.map(item => ({
                                ...item,
                                label: item.display_name
                            }));
                        });
                    break;
                case "wellness-element":
                    promise = vm.$http
                        .get(`${process.env.BARRAMUNDI}inventory/filterItems`, {
                            params: {
                                query: search,
                                module: "wellness_element",
                                ...vm.selectProps
                            },
                            signal: vm.abortController[indexAxios].signal
                        })
                        .then(res => {
                            let options =
                                res.data?.response?.resource?.results ?? [];

                            vm.remote_options = options.map(item => ({
                                ...item,
                                label: item.display_name
                            }));
                        });
                    break;
                default:
                    promise = Promise.resolve(true);
                    break;
            }
            return promise
                .catch(() => {
                    vm.remote_options = [];
                })
                .finally(() => {
                    const isApiAborted =
                        vm.abortController[indexAxios] &&
                        vm.abortController[indexAxios].signal.aborted;

                    if (!isApiAborted) {
                        // prevent aborted API to do anything
                        vm.$emit("options-loaded", vm.remote_options);

                        vm.loading = false;

                        if (vm.search_text.length < 2) {
                            vm.remote_options = [];
                        }
                    }
                });
        }, 350),
        async onChange(value) {
            if (this.module === "payment-method") {
                this.searchModule("", this);

                if (value && value.payment_method_name) {
                    value.label = value.payment_method_name;
                }
            } else if (this.module === "client-patient") {
                if (value && this.clientId) {
                    this.getClientPatient();
                }
            } else if (this.module === "client-patient-all") {
                if (value && this.clientId) {
                    this.getClientPatient(true);
                }
            } else if (this.module === "diagnosis") {
                if (value) {
                    value = {
                        term: value.description,
                        id: value.id,
                        type: 1
                    };
                }
            } else if (this.module === "item") {
                if (value && value.isNewCreated) {
                    const res = await this.getItem(value.id);
                    if (res) {
                        value.label = res.name;
                    }
                }
            }

            if (!this.clearOnSelect) {
                // To let the selected option append in options list
                this.appendSelectedOptiontoOptions(value);
                this.search_text = value ? value.label : "";
            }

            if (this.isAddItemFee) {
                if (value && value.isNewCreated) {
                    const nameDisplay = value.name;
                    const itemTypeCategory = value.item_type_category?.name;
                    const priceDisplay = this._toCurrency(value.price);
                    const itemUomDisplay = value.itemUom?.label;

                    value.label = `${nameDisplay} (${itemTypeCategory}) (${priceDisplay}/${itemUomDisplay})`;
                }
            }

            this.$emit("input", value);
        },
        // Defocus will lost the current text, save it to somewhere else.
        setSearchText(event) {
            this.txt = this.search_text;
            this.$emit("search:blur", event);

            // when the blur event triggered, the options will reset to empty
            // To let the selected option append in options list
            if (!this.clearOnSelect) {
                this.$nextTick(() => {
                    this.appendSelectedOptiontoOptions(this.value);
                });
            }
        },
        appendSelectedOptiontoOptions(value) {
            // By setting the option key to different key, when user reselect the selected option, the input event will be triggered
            if (value) {
                const selectedValue = clone(value); // use clone to differentiate the key from the model in parent
                selectedValue.key = ++this.key;
                this.remote_options = [selectedValue];
            } else {
                this.remote_options = [];
            }
        },
        addClient() {
            let client = {};
            if (this.search_text) {
                let name = humanparser.parseName(this.search_text);
                client = {
                    firstname: name.firstName,
                    lastname: [name.middleName, name.lastName, name.suffix]
                        .join(" ")
                        .trim()
                };
            }
            this.$refs.newClientWindow.open({
                firstname: client.firstname,
                lastname: client.lastname,
                ...this.extra
            });
            this.preventFocus();
        },
        addPatient() {
            if (get(this, "subscription.patient_status") === 1) {
                this._message(this.subscription.patient_message, "warning");
            } else {
                this.$refs.newPatientWindow.open({
                    name: this.search_text,
                    client_id: this.clientId
                });
            }
            this.preventFocus();
        },
        addItem() {
            if (this.isAddItemFee) {
                this.$refs.newItemFeeWindow.open({
                    name: this.search_text
                });
            } else {
                this.$refs.newItemWindow.open({
                    name: this.search_text
                });
            }
            this.preventFocus();
        },
        /**
         * @name getClientPatient
         * @description get patientList
         * @param {Boolean|Array} isAllOrSpecific
         */
        async getClientPatient(isAll = false) {
            let patientDetails = "/patient?patient_actors=true";

            // no status will return all patients
            let params = {};

            if (!isAll) {
                // send status as prop to return only specific status patients
                if (this.status.length > 0) {
                    params.status = this.status;
                } else {
                    // if NOT all,should only active patient
                    params.status = [1];
                }
            }

            const response = await this.$http.get(
                process.env.ACTOR_SERVICE +
                    "client/" +
                    this.clientId +
                    patientDetails,
                { params }
            );

            if (response) {
                this.prefetch_options = get(
                    response,
                    "data.response.resources",
                    []
                );
            } else {
                return [];
            }

            return this.remote_options;
        },
        async getItem(itemId, params) {
            const response = await this.$http.get(
                `${process.env.BARRAMUNDI}inventory/filterItems`,
                {
                    params: {
                        item_id: itemId,
                        module: this.module,
                        ...params
                    }
                }
            );

            if (
                response.data.response.resource &&
                response.data.response.resource.results
            ) {
                return response.data.response.resource.results;
            } else {
                return null;
            }
        },
        addContact() {
            if (this.search_text) {
                let name = humanparser.parseName(this.search_text);
                this.$refs.newContactWindow.open({
                    firstname: name.firstName,
                    lastname: [name.middleName, name.lastName, name.suffix]
                        .join(" ")
                        .trim()
                });
            } else {
                this.$refs.newContactWindow.open({
                    firstname: "",
                    lastname: ""
                });
            }
            this.preventFocus();
        },
        addCompany() {
            if (this.search_text) {
                let name = humanparser.parseName(this.search_text);
                this.$refs.newCompanyWindow.open({
                    name: [
                        name.firstName,
                        name.middleName,
                        name.lastName,
                        name.suffix
                    ]
                        .filter(Boolean) // to remove undefined/null value
                        .join(" ")
                        .trim()
                });
            } else {
                this.$refs.newCompanyWindow.open({
                    name: []
                });
            }
            this.preventFocus();
        },
        addPatientDiagnosis() {
            this.$refs.newPatientDiagnosis.open({
                mode: "NEW",
                placeholder: "Diagnosis (required)",
                saveOnce: true,
                text: this.search_text
            });
        },
        addPaymentMethod() {
            this.$refs.newPaymentMethodWindow.open({
                name: this.search_text,
                client: this.payer,
                is_first_time: this.remote_options.length === 0,
                is_extension: this.isExtension,
                cc_processor: this.ccProcessor,
                trans_type: "payment_method"
            });
            this.preventFocus();
        },
        onboardingAddPaymentMethod() {
            this.$refs.modalPaymentMethodAdd.open();
            this.onboardingPreventFocus();
        },
        updateIsLoadingAddModal(isLoading) {
            this.$emit("toggle-spinner", isLoading);
        },
        updatePetcareOnboardingPaymentMethod() {
            this.$emit("refresh-primary-payment");
        },
        onboardingPreventFocus() {
            this.$nextTick(() => {
                this.$nextTick(() => {
                    $(this.$refs.vselect.$el).find("input").blur();
                });
            });
        },
        preventFocus() {
            let self = this;
            this.$root.$once("bv::modal::hidden", () => {
                self.$nextTick(() => {
                    self.$nextTick(() => {
                        $(self.$refs.vselect.$el).find("input").blur();
                    });
                });
            });
        },
        getEntityBreed(entity) {
            var entityBreed = "";
            // --- loop each breeds
            if (
                entity.patient_breeds &&
                keys(entity.patient_breeds).length > 0
            ) {
                var breeds = [];
                $.each(entity.patient_breeds, function (i, v) {
                    breeds.push(v.name);
                });
                // --- append breeds to species
                if (entity.species && _.keys(entity.species).length > 0) {
                    entityBreed =
                        breeds.join(", ") + " (" + entity.species + ")";
                }
            }

            return entityBreed;
        },
        async getAddressPO() {
            let res = await this.$http.get(
                `${process.env.BARRAMUNDI}inventory/address/list`
            );

            if (res.data.response) {
                let arr = [];
                let rawlist = get(res, "data.response.resources", []);

                for (let id in rawlist) {
                    arr.push({
                        value: id,
                        label: rawlist[id]
                    });
                }

                this.prefetch_options = arr;
            } else {
                this.prefetch_options = [];
            }
        },
        patientNameWithSpecies(details) {
            let str = details.name;
            let species = details?.patient_breeds?.[0]?.species_name ?? null;
            if (species) {
                str += " (" + species + ")";
            }
            return str;
        },
        clear() {
            this.remote_options = [];
            this.search_text = "";
            this.$refs.vselect.$el.querySelector("input").value = ""; // set the input value empty
            this.$refs.vselect.onEscape(); // Clear the search text // https://github.com/sagalbot/vue-select/issues/287 | https://github.com/sagalbot/vue-select/blob/master/src/components/Select.vue
        }
    }
};
</script>
